// frontend/src/pages/KRXIdxValuationBandPage.js
import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import DateRangeControls from '../../components/DateRangeControls';
import useDateRange from '../../hooks/useDateRange';
import { createChartOptions } from '../../utils/chartOptions';
import { Slider, Select, Typography, Card, Divider } from 'antd';
import { SettingOutlined, ToolOutlined } from '@ant-design/icons';
import ResponsiveTypography from '../../components/ResponsiveTypography';

import Loading from '../../components/Loading';

const { Title } = ResponsiveTypography;
const { Text } = Typography;

const DEFAULT_SETTINGS = {
    KOSPI: {
        pbr: { defaultValue: [0.8, 1.2], min: 0.5, max: 2.5, step: 0.1 },
        per: { defaultValue: [8.0, 15.0], min: 5.0, max: 40.0, step: 1.0 }
    },
    KOSDAQ: {
        pbr: { defaultValue: [2.0, 4.0], min: 0.5, max: 5.0, step: 0.1 },
        per: { defaultValue: [15.0, 25.0], min: 5.0, max: 50.0, step: 1.0 }
    },

    KOSPI200: {
        pbr: { defaultValue: [0.8, 1.2], min: 0.5, max: 2.5, step: 0.1 },
        per: { defaultValue: [8.0, 15.0], min: 5.0, max: 40.0, step: 1.0 }
    },
    KOSDAQ150: {
        pbr: { defaultValue: [2.0, 4.0], min: 0.5, max: 5.0, step: 0.1 },
        per: { defaultValue: [30.0, 40.0], min: 5.0, max: 50.0, step: 1.0 }
    }
};

const generateMarks = (min, max, step, multiplier = 10) => {
    const marks = {};
    const markStep = step * multiplier;
    const precision = (step.toString().split('.')[1] || '').length;
    let currentMark = Math.ceil(min / markStep) * markStep;

    while (currentMark <= max) {
        const fixedMark = parseFloat(currentMark.toFixed(precision));
        marks[fixedMark] = null;
        currentMark += markStep;
    }
    return marks;
};

const KRXIdxValuationBandPage = () => {
    const [selectedAsset, setSelectedAsset] = useState('KOSPI200');
    const [bandType, setBandType] = useState('PBR');
    const [pbrRange, setPbrRange] = useState(DEFAULT_SETTINGS.KOSPI200.pbr.defaultValue);
    const [perRange, setPerRange] = useState(DEFAULT_SETTINGS.KOSPI200.per.defaultValue);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(chartData, { dateColumn: 'Date', defaultPeriod: '10Y' });

    useEffect(() => {
        setLoading(true);
        const settings = DEFAULT_SETTINGS[selectedAsset];
        setPbrRange(settings.pbr.defaultValue);
        setPerRange(settings.per.defaultValue);

        apiClient.get(`/valuation/${selectedAsset}`).then(response => {
            setChartData(response.data);
            setLoading(false);
        });
    }, [selectedAsset]);

    const pbrSettings = DEFAULT_SETTINGS[selectedAsset].pbr;
    const perSettings = DEFAULT_SETTINGS[selectedAsset].per;

    const pbrStaticMarks = generateMarks(pbrSettings.min, pbrSettings.max, pbrSettings.step);
    const perStaticMarks = generateMarks(perSettings.min, perSettings.max, perSettings.step);

    const getChartOption = () => {
        if (!isDateReady) return {};

        let range, bandName;
        const closesSeries = { name: '종가', type: 'line', data: chartData.map(d => [d.Date, d.종가]) };
        let lowerBandSeries, upperBandSeries;

        if (bandType === 'PBR') {
            range = pbrRange; bandName = 'PBR';
            lowerBandSeries = { name: `${bandName} x${range[0].toFixed(1)}`, type: 'line', data: chartData.map(d => [d.Date, Math.round(d.book_value * range[0])]) };
            upperBandSeries = { name: `${bandName} x${range[1].toFixed(1)}`, type: 'line', data: chartData.map(d => [d.Date, Math.round(d.book_value * range[1])]) };
        } else {
            range = perRange; bandName = 'PER';
            lowerBandSeries = { name: `${bandName} x${range[0].toFixed(1)}`, type: 'line', data: chartData.map(d => [d.Date, Math.round(d.earnings * range[0])]) };
            upperBandSeries = { name: `${bandName} x${range[1].toFixed(1)}`, type: 'line', data: chartData.map(d => [d.Date, Math.round(d.earnings * range[1])]) };
        }

        return createChartOptions({
            xAxis: { type: 'time' },
            yAxis: { type: 'value', scale: true, log: true, axisLine: { show: true }},
            dataZoom: [
                { type: 'slider', show: true, xAxisIndex: [0], bottom: '3%', startValue: viewStartDate.toISOString(), endValue: viewEndDate.toISOString() },
                { type: 'inside', xAxisIndex: [0] }
            ],
            series: [closesSeries, lowerBandSeries, upperBandSeries],
        });
    };

    if (loading || !isDateReady) {
        return <Loading />;
    }

    return (
        <div className="content-box">
            <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>
                주가 지수 밴드 차트
            </Title>
            <Card style={{ marginTop: '1rem', padding: '0.3125rem' }} bodyStyle={{ padding: '0rem' }}>
                {/* ▼ Space 컴포넌트를 div로 변경하고 직접 Flexbox 스타일을 적용합니다. */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap', // 줄바꿈 허용 (Space의 wrap 속성)
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem', // 아이템 간 간격 (Space의 size="large"에 해당)
                    width: '100%',
                    padding: '0.625rem',
                }}>

                    {/* 첫 번째 자식 div (flex: 1 없음) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Text
                            strong
                            style={{ fontSize: 'inherit', transition: 'font-size 0.2s' }}
                        >
                            <SettingOutlined /> 주가지수{' '}
                        </Text>
                        <Select
                            value={selectedAsset}
                            onChange={value => setSelectedAsset(value)}
                            style={{ width: 110 }}
                            size='small'
                            options={[
                                { value: 'KOSPI', label: 'KOSPI' },
                                { value: 'KOSPI200', label: 'KOSPI200' },
                                { value: 'KOSDAQ150', label: 'KOSDAQ150' }
                            ]}
                        />
                    </div>

                    {/* 두 번째 자식 div (flex: 1 없음) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Text
                            strong
                            style={{ fontSize: '0.875rem', transition: 'font-size 0.2s' }}
                        >
                            <ToolOutlined /> 밴드타입{' '}
                        </Text>
                        <Select
                            value={bandType}
                            onChange={value => setBandType(value)}
                            style={{ width: 70 }}
                            size='small'
                            options={[{ value: 'PBR', label: 'PBR' }, { value: 'PER', label: 'PER' }]}
                        />
                    </div>

                    {/* ▼ 세 번째 자식 div (flex: 1 유지, width: '100%'는 제거해도 됩니다) */}
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 280, gap: '0.25rem' }}>
                        <Text
                            strong
                            style={{ marginRight: '0.3125rem', fontSize: '0.875rem', transition: 'font-size 0.2s' }}
                        >
                            <ToolOutlined /> 밴드 조절
                        </Text>
                        {bandType === 'PBR' ? (
                            <Slider
                                range
                                min={pbrSettings.min}
                                max={pbrSettings.max}
                                step={pbrSettings.step}
                                value={pbrRange}
                                onChange={setPbrRange}
                                marks={{
                                    ...pbrStaticMarks,
                                    [pbrRange[0]]: pbrRange[0].toFixed(1),
                                    [pbrRange[1]]: pbrRange[1].toFixed(1),
                                }}
                                style={{ flex: 1, margin: '0rem 0rem' }}
                            />
                        ) : (
                            <Slider
                                range
                                min={perSettings.min}
                                max={perSettings.max}
                                step={perSettings.step}
                                value={perRange}
                                onChange={setPerRange}
                                marks={{
                                    ...perStaticMarks,
                                    [perRange[0]]: perRange[0].toFixed(1),
                                    [perRange[1]]: perRange[1].toFixed(1),
                                }}
                                style={{ flex: 1, margin: '0rem 0.625rem' }}
                            />
                        )}
                    </div>
                </div>
            </Card>

            <Card style={{ marginTop: '1rem', padding: '0.625rem' }} bodyStyle={{ padding: '0rem' }}>
                <Title 
                    level={4} 
                    style={{ 
                        textAlign: 'left', 
                        marginTop: '0rem', 
                        marginBottom: '0rem', 
                        padding: '0rem', 
                    }}
                >
                    {selectedAsset} {bandType} 밴드 차트
                </Title>
                <Divider style={{ marginTop: '0.625rem', marginBottom: '0.625rem' }} />
                <ReactECharts option={getChartOption()} style={{ height: '25rem', marginTop: '0.625rem', padding: '0rem'}} showLoading={loading} />
                <Divider style={{ marginTop: '0.625rem', marginBottom: '1.25rem' }} />

                <DateRangeControls minDate={minDate} maxDate={maxDate} viewStartDate={viewStartDate} viewEndDate={viewEndDate} onPeriodChange={handlePeriodChange} onDateChange={handleDateChange} />
            </Card>
        </div>
    );
};

export default KRXIdxValuationBandPage;